import type { FC } from 'react'
import type { Reward, CreateRewardDTO, UpdateRewardDTO, RewardCategoryWithRewards } from 'types/rewards'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Box } from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import { TextField } from 'components/@extended/TextField'
import { useEffect, useState, useMemo } from 'react'

interface RewardDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: CreateRewardDTO | UpdateRewardDTO) => void
  reward: Reward | null
  categoryId: string
  categories: RewardCategoryWithRewards[] | null
  isLoading: boolean
  organizationId: string
}

const RewardDialog: FC<RewardDialogProps> = ({
  open,
  onClose,
  onSubmit,
  reward,
  categoryId,
  categories,
  isLoading,
  organizationId,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    supportsEmployeeContribution: false,
  })

  // Get category name for display
  const categoryName = useMemo(() => {
    if (!categories) return ''
    const effectiveCategoryId = reward?.categoryId || categoryId
    const category = categories.find((cat) => cat.id === effectiveCategoryId)
    return category?.name || ''
  }, [categories, reward, categoryId])

  useEffect(() => {
    if (open) {
      setFormData({
        name: reward?.name || '',
        supportsEmployeeContribution: reward?.supportsEmployeeContribution || false,
      })
    }
  }, [open, reward])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      supportsEmployeeContribution: e.target.checked,
    })
  }

  const handleSubmit = () => {
    if (reward) {
      // Update - only update name and supportsEmployeeContribution
      const updateDto: UpdateRewardDTO = {
        name: formData.name,
        supportsEmployeeContribution: formData.supportsEmployeeContribution,
      }
      onSubmit(updateDto)
    } else {
      // Create - include categoryId, organizationIds, and default displayOrder to 100
      const createDto: CreateRewardDTO = {
        rootOrganizationId: organizationId,
        organizationId: organizationId,
        categoryId,
        name: formData.name,
        displayOrder: 100,
        supportsEmployeeContribution: formData.supportsEmployeeContribution,
      }
      onSubmit(createDto)
    }
  }

  const isValid = formData.name.trim() !== ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{reward ? 'Edit Reward' : 'Create Reward'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Category" variant="outlined" pill shrink fullWidth value={categoryName} disabled />

          <TextField
            autoFocus
            name="name"
            label="Reward Name *"
            variant="outlined"
            pill
            shrink
            fullWidth
            value={formData.name}
            onChange={handleChange}
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.supportsEmployeeContribution}
                onChange={handleSwitchChange}
                name="supportsEmployeeContribution"
              />
            }
            label="Supports Employee Contribution"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <AnimateButton>
          <Button variant="contained" color="warning" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </AnimateButton>

        <AnimateButton>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading || !isValid}>
            {isLoading ? 'Saving...' : reward ? 'Update' : 'Save'}
          </Button>
        </AnimateButton>
      </DialogActions>
    </Dialog>
  )
}

export default RewardDialog
